import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Save, Moon, Sun, ExternalLink, Shield } from 'lucide-react';
import { useAuth } from '@/core/auth-context';
import { useTheme } from '@/lib/theme';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import packageJson from '../../package.json';

export const Route = createFileRoute('/settings')({
  head: () => ({
    meta: [{ title: 'Impostazioni · Replya Internal Backoffice' }],
  }),
  component: SettingsPage,
});

function SettingsPage() {
  const { username, adminApiKey, logout, updateAdminApiKey } = useAuth();
  const { theme, setTheme } = useTheme();
  const [apiKeyInput, setApiKeyInput] = useState(adminApiKey ?? '');

  const handleSaveApiKey = () => {
    updateAdminApiKey(apiKeyInput.trim() || null);
    toast.success('API key aggiornata');
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader title="Settings" subtitle="Impostazioni del backoffice." />

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="rounded-2xl border bg-surface">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="h-4 w-4 text-brand" />
              Account
            </CardTitle>
            <CardDescription>Informazioni sulla sessione corrente</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Username:</span>{' '}
                <span className="font-medium text-foreground">{username}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Admin API Key:</span>{' '}
                <span className="font-medium text-foreground">
                  {adminApiKey ? 'Configurata' : 'Non configurata'}
                </span>
              </div>
            </div>
            <Button variant="outline" onClick={logout} className="rounded-xl">
              Logout
            </Button>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border bg-surface">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              {theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              Aspetto
            </CardTitle>
            <CardDescription>Scegli il tema dell'interfaccia</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Switch
                id="theme"
                checked={theme === 'dark'}
                onCheckedChange={(checked: boolean) => setTheme(checked ? 'dark' : 'light')}
              />
              <Label htmlFor="theme" className="text-sm">
                Tema scuro
              </Label>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border bg-surface md:col-span-2">
          <CardHeader>
            <CardTitle>Admin API Key</CardTitle>
            <CardDescription>Aggiorna la chiave API usata per le chiamate al backend</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="password"
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                placeholder="Inserisci la Admin API Key"
                className="rounded-xl bg-surface-2 border-border sm:flex-1"
              />
              <Button onClick={handleSaveApiKey} className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground">
                <Save className="mr-2 h-4 w-4" />
                Salva
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border bg-surface md:col-span-2">
          <CardHeader>
            <CardTitle>Ambiente</CardTitle>
            <CardDescription>Configurazione corrente e informazioni</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <span className="text-muted-foreground">API Base URL:</span>{' '}
              <span className="font-medium text-foreground">
                {import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Modalità:</span>{' '}
              <span className="font-medium text-foreground">
                {import.meta.env.DEV ? 'Sviluppo' : 'Produzione'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Versione:</span>{' '}
              <span className="font-medium text-foreground">{packageJson.version}</span>
            </div>
            <Button variant="outline" className="rounded-xl" asChild>
              <Link to="/" target="_blank">
                Apri dashboard <ExternalLink className="ml-2 h-3 w-3" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
